import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  ITenantsRepository,
  Tenant,
  CreateTenantInput,
} from '@erp/data-access/interfaces';

interface TenantRow {
  id: string;
  name: string;
  subdomain: string;
  custom_domain: string | null;
  mf: string | null;
  phone: string | null;
  email: string | null;
  owner_user_id: string;
  plan: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Implementation of ITenantsRepository against the master Supabase project.
 *  All queries hit the same `tenants` table; RLS handles the authentication
 *  context (owner sees their tenant, anon can see active tenants for
 *  hostname resolution from the storefront). */
export class SupabaseTenantsRepository implements ITenantsRepository {
  constructor(private supabase: SupabaseClient) {}

  async getCurrent(): Promise<Tenant | null> {
    // For regular users RLS already scopes tenants to their own tenant_id,
    // so a `limit-1` would be enough. But platform admins see every
    // tenant — `limit(1)` would silently return whichever row Postgres
    // picks first, masking which tenant the rest of the app is operating
    // as. Filter explicitly by current_user_tenant_id() so admin and
    // regular users converge on the same tenant the BEFORE-INSERT
    // triggers will write to.
    type SbAny = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: string | null; error: { message: string } | null }> };
    const { data: tenantId } = await (this.supabase as unknown as SbAny).rpc(
      'current_user_tenant_id',
      {},
    );
    if (!tenantId) return null;
    const { data, error } = await this.supabase
      .from('tenants')
      .select('*')
      .eq('id', tenantId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapTenant(data as TenantRow) : null;
  }

  async getByHostname(host: string): Promise<Tenant | null> {
    const cleaned = host.trim().toLowerCase().replace(/^www\./, '');
    // Try the leftmost label as a Lyfty subdomain first (the common case),
    // then fall back to the full host as a custom domain. Two requests
    // sequentially so an "or" filter doesn't fight RLS predicates.
    const subdomain = cleaned.split('.')[0];

    // Try subdomain match
    const sub = await this.supabase
      .from('tenants')
      .select('*')
      .ilike('subdomain', subdomain ?? '')
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    if (sub.data) return mapTenant(sub.data as TenantRow);

    // Try custom domain match
    const custom = await this.supabase
      .from('tenants')
      .select('*')
      .or(`custom_domain.ilike.${cleaned},custom_domain.ilike.www.${cleaned}`)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    if (custom.data) return mapTenant(custom.data as TenantRow);

    return null;
  }

  async create(input: CreateTenantInput): Promise<{ tenantId: string }> {
    // The RPC normalises and trims everything server-side; we only
    // pre-clean the keys we forward as JSON so the wizard's blank
    // strings turn into real `null`s. (Server-side `NULLIF(trim(...))`
    // also handles this, but keeping the wire payload clean makes the
    // PostgREST request log readable.)
    const detailsRaw = input.details ?? {};
    const details: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(detailsRaw)) {
      if (typeof v === 'string') {
        const t = v.trim();
        if (t.length > 0) details[k] = t;
      } else if (v != null) {
        // Stringify non-string non-null values defensively (shouldn't happen).
        details[k] = String(v);
      }
    }

    const { data, error } = await this.supabase.rpc('create_tenant_for_current_user', {
      p_name: input.name,
      p_subdomain: input.subdomain,
      p_mf: input.mf ?? null,
      p_phone: input.phone ?? null,
      p_email: input.email ?? null,
      p_details: details,
      p_modules: input.modules ?? [],
    });
    if (error) throw error;
    return { tenantId: data as string };
  }

  async isSubdomainTaken(subdomain: string): Promise<boolean> {
    const v = subdomain.trim().toLowerCase();
    if (!v) return false;
    // Call the SECURITY DEFINER RPC — a plain SELECT on `tenants` is
    // hidden by RLS for users who don't yet have a tenant_member row
    // (the entire onboarding audience), so it would falsely report
    // "available" for every subdomain. See migration 20260426100000.
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: boolean | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'is_subdomain_taken',
      { p_subdomain: v },
    );
    if (error) throw error;
    return data === true;
  }
}

function mapTenant(row: TenantRow): Tenant {
  return {
    id: row.id,
    name: row.name,
    subdomain: row.subdomain,
    customDomain: row.custom_domain,
    mf: row.mf,
    phone: row.phone,
    email: row.email,
    ownerUserId: row.owner_user_id,
    plan: row.plan,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
